import React from 'react';

import Box from '@mui/material/Box';

// Styles
import { useTheme } from '@mui/material/styles';

// Components
import MealsGrid from './MealsGrid';
import MealsHeader from './MealsHeader';
import MealsMobile from './MealsMobile';

import {
  HTTPHeaders
} from "../../interfaces";

import {
  DateRange,
  RequestMeal
} from '../../models';

import {
  API_URL
} from '../../global';

// Auth0 user hooks 
import { useAuth0User } from '../../hooks/UseAuth0User';

// Responsive hooks
import { useResponsive } from '../../hooks/UseResponsive';

interface MealsProps {
  dateRange: DateRange;
  nights: number;
}

const Meals: React.FC<MealsProps> = (props) => {

  const { getAccessToken } = useAuth0User();
  const { isMobile } = useResponsive();

  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [rows, setRows] = React.useState<Array<RequestMeal>>([]);

  // #region Styles

  const theme = useTheme();
  const styles = {
    gridContainer: {
      flexGrow: 1,
      height: 370,
      padding: theme.spacing(0, 1, 0, 1),
    }
  };

  // #endregion

  // #region Methods

  /*
 * Fetch request meals based upon filter
 */
  const fetchMeals = async () => {

    setIsLoading(true);

    // Auth0 API token
    const accessToken = await getAccessToken();

    const headers: HTTPHeaders = {};
    headers['Content-Type'] = 'application/json';
    headers['Accept'] = 'application/json';
    headers['Authorization'] = `Bearer ${accessToken}`;

    // Always show current date forward
    const params = new URLSearchParams();
    if (props.dateRange !== undefined) {
      if (props.dateRange.startDate) {
        params.append('startDate', props.dateRange.startDate);
      }
      if (props.dateRange.endDate) {
        params.append('endDate', props.dateRange.endDate);
      }
    }

    const url = new URL(`${API_URL}/v1/requests/requestmeals`);
    url.search = params.toString();

    await fetch(url.toString(), {
      method: 'GET',
      cache: 'no-cache',
      headers: headers,
      mode: 'cors'
    })
      .then((response) => {
        if (!response.ok) {
          setIsLoading(false);
          throw new Error('Request passes not retrieved');
        }
        return response.json()
      })
      .then((gridData: Array<RequestMeal>) => {
        setRows(gridData);
        setIsLoading(false);
      })
      .catch(err => {
        setIsLoading(false);
      });
  }

  // #endregion

  // #region Event handlers

  React.useEffect(() => {
    if (props.dateRange.startDate && props.dateRange.endDate) {
      fetchMeals();
    } else {
      setRows([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.dateRange.startDate, props.dateRange.endDate]);

  // #endregion

  return (
    <>
      <MealsHeader />
      {!isMobile &&
        <Box sx={styles.gridContainer}>
          <MealsGrid
            dateRange={props.dateRange}
            isLoading={isLoading}
            nights={props.nights}
            rows={rows}
          />
        </Box>
      }
      {isMobile &&
        <MealsMobile
          dateRange={props.dateRange}
          nights={props.nights}
          rows={rows}
        />
      }
    </>
  );

}

export default Meals;